import React, { useState, useEffect } from 'react';
import { rptV3Api } from '../services/api';
import RPTV3GLNatureMappingEditor from './RPTV3GLNatureMappingEditor';
import '../styles/Modal.css';

/**
 * Modal for generating RPT V3 (Rule-based CPM RPT Analyzer)
 * 
 * RPT V3 Features:
 * - Rule-based classification (no AI dependency)
 * - 14 RPT nature categories
 * - Entity-specific configurations
 * - Formula-driven Excel output with SUMIFS
 * - Detailed audit trail
 */
function GenerateRPTV3Modal({ entity, glVersion, onClose, onComplete }) {
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const [natures, setNatures] = useState([]);
  const [mappingConfig, setMappingConfig] = useState(null);

  useEffect(() => {
    loadNatures();
  }, []);

  const loadNatures = async () => {
    try {
      const response = await rptV3Api.getNatures();
      setNatures(response.natures || []);
    } catch (err) {
      console.error('Failed to load natures:', err);
    }
  };

  const handleGenerate = async () => {
    setGenerating(true);
    setError(null);
    setResult(null);

    try {
      const [startDate, endDate] = glVersion.period.split('_to_');
      
      const response = await rptV3Api.generateRPT(
        entity.name,
        startDate,
        endDate
      );

      setResult(response);

      if (onComplete) {
        onComplete(response);
      }
    } catch (err) {
      console.error('Generation error:', err);
      setError(err.message || err.data?.error || 'Failed to generate RPT V3');
    } finally {
      setGenerating(false);
    }
  };

  const handleDownload = async () => {
    if (!result?.storage) return;

    try {
      const blob = await rptV3Api.downloadRPT(
        entity.name,
        result.storage.timestamp,
        result.storage.period
      );

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `RPT_V3_${entity.name}_${result.storage.period}_${result.storage.timestamp}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      console.error('Download error:', err);
      setError('Failed to download file');
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content modal-lg" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Generate RPT V3</h2>
          <button className="close-btn" onClick={onClose}>&times;</button>
        </div>

        <div className="modal-body">
          {/* Info Section */}
          <div className="info-section info-v3">
            <h3>RPT V3 - Rule-Based Classification</h3>
            <p>
              RPT V3 uses comprehensive rule-based classification derived from the CPM RPT analyzer.
              This provides deterministic and auditable RPT identification without AI dependency.
            </p>
            <ul className="feature-list">
              <li>Comprehensive Related Party pattern matching</li>
              <li>Account-based transaction classification</li>
              <li>Entity-specific configurations (exclusions, currency)</li>
              <li>Formula-driven Excel output with SUMIFS</li>
              <li>Detailed audit trail for every decision</li>
            </ul>
          </div>

          {/* GL Version Info */}
          <div className="section">
            <h4>Source GL Data</h4>
            <div className="info-grid">
              <div className="info-item">
                <label>Entity:</label>
                <span>{entity.description || entity.name}</span>
              </div>
              <div className="info-item">
                <label>Period:</label>
                <span>{glVersion.period}</span>
              </div>
              <div className="info-item">
                <label>GL Timestamp:</label>
                <span>{glVersion.timestamp}</span>
              </div>
              <div className="info-item">
                <label>Total Entries:</label>
                <span>{glVersion.record_count?.toLocaleString() || 'N/A'}</span>
              </div>
            </div>
          </div>

          <RPTV3GLNatureMappingEditor
            entity={entity}
            natures={natures}
            onMappingsChanged={setMappingConfig}
          />

          {/* Natures Section */}
          {natures.length > 0 && (
            <div className="section">
              <h4>Supported RPT Natures ({natures.length})</h4>
              <div className="nature-tags">
                {natures.map((nature, idx) => (
                  <span key={idx} className="nature-tag">{nature}</span>
                ))}
              </div>
            </div>
          )}

          {/* Error Display */}
          {error && (
            <div className="alert alert-danger">
              <strong>Error:</strong> {error}
            </div>
          )}

          {/* Result Display */}
          {result && result.success && (
            <div className="result-section">
              <div className="alert alert-success">
                <strong>RPT V3 Generated Successfully!</strong>
              </div>

              <div className="summary-grid">
                <div className="summary-item">
                  <span className="summary-label">Total GL Entries</span>
                  <span className="summary-value">{result.rpt_summary?.total_gl_entries?.toLocaleString()}</span>
                </div>
                <div className="summary-item">
                  <span className="summary-label">RPT Entries Identified</span>
                  <span className="summary-value highlight">{result.rpt_summary?.rpt_entries_identified?.toLocaleString()}</span>
                </div>
                <div className="summary-item">
                  <span className="summary-label">Unique Related Parties</span>
                  <span className="summary-value">{result.rpt_summary?.unique_related_parties}</span>
                </div>
                <div className="summary-item">
                  <span className="summary-label">Unique Natures</span>
                  <span className="summary-value">{result.rpt_summary?.unique_natures}</span>
                </div>
                <div className="summary-item">
                  <span className="summary-label">Aggregated Entries</span>
                  <span className="summary-value">{result.rpt_summary?.aggregated_entries}</span>
                </div>
                <div className="summary-item">
                  <span className="summary-label">Reporting Currency</span>
                  <span className="summary-value">{result.rpt_summary?.reporting_currency}</span>
                </div>
                <div className="summary-item">
                  <span className="summary-label">User Mapping Count</span>
                  <span className="summary-value">{result.user_gl_nature_mapping_config?.active_count || 0}</span>
                </div>
              </div>

              <div className="mapping-snapshot">
                <strong>Mapping config hash:</strong>{' '}
                {result.user_gl_nature_mapping_config?.config_hash || mappingConfig?.config_hash || 'N/A'}
              </div>

              {result.stats && (
                <div className="stats-section">
                  <h5>Processing Statistics</h5>
                  <div className="stats-grid">
                    <div className="stat-item">
                      <span>Excluded (forex/balance sheet):</span>
                      <span>{result.stats.excluded_count?.toLocaleString() || 0}</span>
                    </div>
                    <div className="stat-item">
                      <span>No RP Match:</span>
                      <span>{result.stats.no_rp_count?.toLocaleString() || 0}</span>
                    </div>
                    <div className="stat-item">
                      <span>No Nature Classification:</span>
                      <span>{result.stats.no_nature_count?.toLocaleString() || 0}</span>
                    </div>
                  </div>
                </div>
              )}

              <div className="download-section">
                <button className="btn btn-success btn-lg" onClick={handleDownload}>
                  Download RPT V3 Excel
                </button>
              </div>
            </div>
          )}
        </div>

        <div className="modal-footer">
          {!result ? (
            <>
              <button className="btn btn-secondary" onClick={onClose} disabled={generating}>
                Cancel
              </button>
              <button
                className="btn btn-v3"
                onClick={handleGenerate}
                disabled={generating}
              >
                {generating ? (
                  <>
                    <span className="spinner-sm"></span>
                    Generating...
                  </>
                ) : (
                  'Generate RPT V3'
                )}
              </button>
            </>
          ) : (
            <button className="btn btn-secondary" onClick={onClose}>
              Close
            </button>
          )}
        </div>
      </div>

      <style jsx>{`
        .modal-lg {
          max-width: 700px;
        }
        .info-v3 {
          background: linear-gradient(135deg, #f0fdf4, #dcfce7);
          border-left: 4px solid #22c55e;
          padding: 15px;
          border-radius: 4px;
          margin-bottom: 20px;
        }
        .info-v3 h3 {
          color: #16a34a;
          margin: 0 0 10px 0;
          font-size: 16px;
        }
        .info-v3 p {
          color: #166534;
          font-size: 13px;
          margin: 0 0 10px 0;
        }
        .feature-list {
          list-style: none;
          padding: 0;
          margin: 0;
        }
        .feature-list li {
          padding: 3px 0;
          font-size: 12px;
          color: #16a34a;
        }
        .feature-list li::before {
          content: '✓ ';
          font-weight: bold;
        }
        .section {
          margin-top: 20px;
        }
        .section h4 {
          margin: 0 0 10px 0;
          font-size: 14px;
          color: #333;
          border-bottom: 1px solid #eee;
          padding-bottom: 8px;
        }
        .info-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 10px;
        }
        .info-item {
          display: flex;
          flex-direction: column;
        }
        .info-item label {
          font-size: 11px;
          color: #666;
          text-transform: uppercase;
        }
        .info-item span {
          font-size: 14px;
          color: #333;
          font-weight: 500;
        }
        .nature-tags {
          display: flex;
          flex-wrap: wrap;
          gap: 6px;
        }
        .nature-tag {
          background: #e0f2fe;
          color: #0369a1;
          padding: 3px 8px;
          border-radius: 12px;
          font-size: 11px;
        }
        .summary-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 15px;
          margin-top: 15px;
        }
        .summary-item {
          background: #f8f9fa;
          padding: 12px;
          border-radius: 8px;
          text-align: center;
        }
        .summary-label {
          display: block;
          font-size: 11px;
          color: #666;
          margin-bottom: 5px;
        }
        .summary-value {
          display: block;
          font-size: 18px;
          font-weight: 600;
          color: #333;
        }
        .summary-value.highlight {
          color: #22c55e;
        }
        .mapping-snapshot {
          margin-top: 12px;
          padding: 10px;
          border-radius: 6px;
          background: #f0fdf4;
          color: #166534;
          font-size: 12px;
        }
        .stats-section {
          margin-top: 15px;
          padding: 12px;
          background: #fafafa;
          border-radius: 8px;
        }
        .stats-section h5 {
          margin: 0 0 10px 0;
          font-size: 12px;
          color: #666;
        }
        .stats-grid {
          display: flex;
          flex-direction: column;
          gap: 5px;
        }
        .stat-item {
          display: flex;
          justify-content: space-between;
          font-size: 12px;
          color: #555;
        }
        .download-section {
          margin-top: 20px;
          text-align: center;
        }
        .btn-v3 {
          background: linear-gradient(135deg, #22c55e, #16a34a);
          color: white;
          border: none;
          padding: 10px 24px;
          border-radius: 6px;
          font-weight: 600;
          cursor: pointer;
          display: inline-flex;
          align-items: center;
          gap: 8px;
        }
        .btn-v3:hover {
          background: linear-gradient(135deg, #16a34a, #15803d);
        }
        .btn-v3:disabled {
          opacity: 0.7;
          cursor: not-allowed;
        }
        .btn-lg {
          padding: 12px 30px;
          font-size: 15px;
        }
        .spinner-sm {
          width: 16px;
          height: 16px;
          border: 2px solid rgba(255,255,255,0.3);
          border-top: 2px solid white;
          border-radius: 50%;
          animation: spin 1s linear infinite;
        }
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        .alert-success {
          background: #dcfce7;
          border: 1px solid #86efac;
          color: #166534;
          padding: 12px;
          border-radius: 6px;
        }
        .alert-danger {
          background: #fee2e2;
          border: 1px solid #fca5a5;
          color: #991b1b;
          padding: 12px;
          border-radius: 6px;
          margin-bottom: 15px;
        }
      `}</style>
    </div>
  );
}

export default GenerateRPTV3Modal;
